import type { FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Provider, useDispatch } from 'react-redux';

import type { AppDispatch } from '../../../store';
import { store } from '../../../store';
import { useAppSelector } from '../../../store/hooks';
import { isAndroidMobile, isAppleMobile } from '../../../utils/platform.utils';
import { WalletType } from '../../models/membership.model';
import { selectMemberships } from '../../store/membership.selectors';
import { downloadWalletPass, loadMemberships } from '../../store/membership.thunk';

export interface MembershipPassDownloadProps {
  membershipId: string;
}

/**
 * Redirect to perks/codes page
 */
function loadPerks(): void {
  window.location.href = `${window.location.origin}/apps/single/membership#/codes`;
}

const MembershipPassDownloadComponent: FunctionComponent<MembershipPassDownloadProps> = ({
  membershipId,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Redux selectors
  const memberships = useAppSelector(selectMemberships);

  // Track if side effect has been executed (replaces take(1))
  const [effectExecuted, setEffectExecuted] = useState(false);

  // Load memberships on mount
  useEffect(() => {
    dispatch(loadMemberships());
  }, [dispatch]);

  // Check if data is ready
  const dataReady = useMemo(() => {
    return memberships && memberships.length > 0;
  }, [memberships]);

  // Execute side effect when data is ready (replaces filter + take(1) + tap)
  useEffect(() => {
    if (!dataReady || effectExecuted) return;

    // Find membership with wallet theme
    const membership = memberships?.find(
      (m) => m.id === membershipId && !!m.contract?.tier?.walletTheme
    );

    if (!membership?.contract) {
      // No valid membership found, redirect to perks
      loadPerks();
      setEffectExecuted(true);
      return;
    }

    // Trigger platform-specific download or redirect
    if (isAndroidMobile()) {
      // Android: Download Google Wallet pass
      dispatch(
        downloadWalletPass({
          contract: membership.contract,
          walletType: WalletType.GOOGLE,
        })
      );
    } else if (isAppleMobile()) {
      // iOS: Download Apple Wallet pass
      dispatch(
        downloadWalletPass({
          contract: membership.contract,
          walletType: WalletType.APPLE,
        })
      );
    } else {
      // Desktop: Redirect to perks
      loadPerks();
    }

    // Mark effect as executed (replaces take(1))
    setEffectExecuted(true);
  }, [dataReady, effectExecuted, memberships, membershipId, dispatch]);

  // Component has no UI - returns null
  return null;
};

// Memoize component to prevent unnecessary re-renders
export const MembershipPassDownload = memo(MembershipPassDownloadComponent);

// Wrapper with Redux Provider for standalone widget usage
export const MembershipPassDownloadWithStore: FunctionComponent<MembershipPassDownloadProps> = (
  props
) => {
  return (
    <Provider store={store}>
      <MembershipPassDownload {...props} />
    </Provider>
  );
};
